/**
 * Deployments — Superadmin view of build SHA and deployment age across all environments.
 * Polls the /api/health endpoint of each service every 30 seconds.
 *
 * Route: /admin/deployments
 */
import React, { useEffect, useState, useCallback } from "react";

const GITHUB_REPO = "velaro/velaro-admin";

interface BuildInfo {
  sha: string;
  builtAt: string;
  shortSha: string;
  ageMinutes: number | null;
}

interface HealthResponse {
  service?: string;
  status: string;
  timestamp: string;
  build?: BuildInfo;
}

interface EnvCard {
  label: string;
  service: "admin" | "messaging";
  url: string;
  env: "staging" | "production";
  slotName?: string;
  azureState?: "Running" | "Stopped";
}

// Hostnames verified via: az webapp deployment slot list / az webapp show
const ENVIRONMENTS: EnvCard[] = [
  // ── Staging (separate App Services, not slots of prod) ───────────────────────
  {
    label: "Admin",
    service: "admin",
    env: "staging",
    url: "https://velaro-admin-staging.azurewebsites.net/api/health"
  },
  {
    label: "Messaging",
    service: "messaging",
    env: "staging",
    url: "https://velaro-messaging-api-staging.azurewebsites.net/api/health"
  },
  // ── Production live slots ────────────────────────────────────────────────────
  {
    label: "Admin",
    service: "admin",
    env: "production",
    slotName: "production (live)",
    azureState: "Running",
    url: "https://velaro-admin-prod.azurewebsites.net/api/health"
  },
  {
    label: "Messaging",
    service: "messaging",
    env: "production",
    slotName: "production (live)",
    azureState: "Running",
    url: "https://velaro-messaging-api-prod.azurewebsites.net/api/health"
  },
  // ── Production deployment slots (green/blue) ─────────────────────────────────
  {
    label: "Admin",
    service: "admin",
    env: "production",
    slotName: "velaro-admin-prod-green",
    azureState: "Running",
    url: "https://velaro-admin-prod-velaro-admin-prod-green.azurewebsites.net/api/health"
  },
  {
    label: "Messaging",
    service: "messaging",
    env: "production",
    slotName: "velaro-messaging-api-prod-green",
    azureState: "Running",
    url: "https://velaro-messaging-api-prod-velaro-messaging-api-prod-green.azurewebsites.net/api/health"
  },
  {
    label: "Admin",
    service: "admin",
    env: "production",
    slotName: "admin-prod-green",
    azureState: "Stopped",
    url: "https://velaro-admin-prod-admin-prod-green.azurewebsites.net/api/health"
  },
];

// ─── SignalR section ───────────────────────────────────────────────────────────

interface SignalRSlot {
  label: string;
  negotiateUrl: string;
  statusUrl: string;
}

// Slots verified via: az webapp deployment slot list --name signalr-engagement-us-east-prod
const SIGNALR_SLOTS: SignalRSlot[] = [
  {
    label: "Live",
    negotiateUrl: "https://signalr-engagement-us-east-prod.azurewebsites.net/signalr/negotiate?clientProtocol=2.1",
    statusUrl: "https://signalr-engagement-us-east-prod.azurewebsites.net/status",
  },
  {
    label: "staging slot",
    negotiateUrl: "https://signalr-engagement-us-east-prod-staging.azurewebsites.net/signalr/negotiate?clientProtocol=2.1",
    statusUrl: "https://signalr-engagement-us-east-prod-staging.azurewebsites.net/status",
  },
  {
    label: "september slot",
    negotiateUrl: "https://signalr-engagement-us-east-prod-september.azurewebsites.net/signalr/negotiate?clientProtocol=2.1",
    statusUrl: "https://signalr-engagement-us-east-prod-september.azurewebsites.net/status",
  },
];

interface SignalRProbeResult {
  loading: boolean;
  httpStatus: number | null;
  ok: boolean | null;
  preview: string | null;
  error: string | null;
  lastChecked: Date | null;
}

function useSignalRProbe(slot: SignalRSlot, active: boolean) {
  const [state, setState] = React.useState<SignalRProbeResult>({
    loading: false, httpStatus: null, ok: null, preview: null, error: null, lastChecked: null
  });

  const probe = React.useCallback(async () => {
    if (!active) return;
    setState(prev => ({ ...prev, loading: true }));
    try {
      const resp = await fetch(slot.negotiateUrl, { signal: AbortSignal.timeout(10_000) });
      let preview: string | null = null;
      try {
        const text = await resp.text();
        const trimmed = text.trim();
        preview = trimmed.length > 200 ? trimmed.slice(0, 200) + "…" : trimmed;
      } catch { /* ignore */ }
      setState({
        loading: false,
        httpStatus: resp.status,
        ok: resp.ok,
        preview,
        error: null,
        lastChecked: new Date(),
      });
    } catch (e) {
      setState({
        loading: false,
        httpStatus: null,
        ok: false,
        preview: null,
        error: String(e),
        lastChecked: new Date(),
      });
    }
  }, [slot.negotiateUrl, active]);

  React.useEffect(() => {
    if (!active) return;
    probe();
    const iv = setInterval(probe, 30_000);
    return () => clearInterval(iv);
  }, [probe, active]);

  return { state, probe };
}

function SignalRSlotCard({ slot, active }: { slot: SignalRSlot; active: boolean }) {
  const { state, probe } = useSignalRProbe(slot, active);

  let borderColor = "border-slate-200";
  let headerBg = "bg-slate-50";
  if (state.ok === true) { borderColor = "border-green-300"; headerBg = "bg-green-50"; }
  else if (state.ok === false) { borderColor = "border-red-300"; headerBg = "bg-red-50"; }

  return (
    <div className={`border-2 ${borderColor} rounded-lg overflow-hidden shadow-sm`}>
      <div className={`${headerBg} px-4 py-3 flex items-center justify-between`}>
        <span className="font-semibold text-sm text-slate-800">{slot.label}</span>
        {state.loading && state.lastChecked == null ? (
          <span className="text-xs text-slate-400">Probing…</span>
        ) : state.ok === true ? (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-800">
            {state.httpStatus} OK
          </span>
        ) : state.ok === false ? (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-red-100 text-red-700">
            {state.httpStatus ?? "Error"}
          </span>
        ) : (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500">
            Pending
          </span>
        )}
      </div>
      <div className="px-4 py-3 space-y-2 text-sm">
        {state.error && (
          <p className="text-red-600 text-xs">{state.error}</p>
        )}
        {state.preview && (
          <div>
            <span className="text-slate-500 text-xs block mb-1">Negotiate response</span>
            <pre className="text-xs font-mono text-slate-700 bg-slate-50 border border-slate-200 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all">
              {state.preview}
            </pre>
          </div>
        )}
        <div className="flex items-center gap-2">
          <span className="text-slate-400 text-xs w-20 shrink-0">Endpoint</span>
          <span className="font-mono text-xs text-slate-500 break-all">{slot.negotiateUrl}</span>
        </div>
        {state.lastChecked && (
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            <span className="text-slate-400 w-20 shrink-0 text-xs">Checked</span>
            <span className="text-slate-400 text-xs">{formatRelative(state.lastChecked)}</span>
            <button onClick={probe} className="ml-auto text-xs text-blue-500 hover:underline">
              Refresh
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SignalRPanel() {
  const [activeSlotIndex, setActiveSlotIndex] = React.useState(0);

  return (
    <div className="mt-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider">
          SignalR (v10 Production)
        </h2>
        <div className="flex rounded-md border border-slate-300 overflow-hidden text-xs">
          {SIGNALR_SLOTS.map((slot, i) => (
            <button
              key={slot.label}
              onClick={() => setActiveSlotIndex(i)}
              className={`px-3 py-1.5 font-medium transition-colors ${
                activeSlotIndex === i
                  ? "bg-blue-600 text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"
              }`}
            >
              {slot.label}
            </button>
          ))}
        </div>
      </div>
      <SignalRSlotCard slot={SIGNALR_SLOTS[activeSlotIndex]} active={true} />
    </div>
  );
}

const GITHUB_REPOS: Record<"admin" | "messaging", string> = {
  admin: "velaro/velaro-admin",
  messaging: "velaro/velaro-messaging"
};

const REFRESH_INTERVAL_MS = 30_000;

interface CardState {
  loading: boolean;
  error: string | null;
  data: HealthResponse | null;
  lastChecked: Date | null;
}

function formatRelative(date: Date): string {
  const diffMs = Date.now() - date.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHrs = Math.floor(diffMin / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  return `${Math.floor(diffHrs / 24)}d ago`;
}

function formatAge(minutes: number | null | undefined): string {
  if (minutes == null) return "unknown";
  if (minutes < 60) return `${minutes}m`;
  const hrs = Math.floor(minutes / 60);
  if (hrs < 24) return `${hrs}h ${minutes % 60}m`;
  const days = Math.floor(hrs / 24);
  return `${days}d ${hrs % 24}h`;
}

function formatAbsolute(iso: string): string {
  if (!iso || iso === "unknown") return "unknown";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function StatusBadge({ status }: { status: string }) {
  const base = "inline-block px-2 py-0.5 rounded text-xs font-semibold";
  if (status === "Healthy") {
    return <span className={`${base} bg-green-100 text-green-800`}>Healthy</span>;
  }
  if (status === "Degraded") {
    return <span className={`${base} bg-yellow-100 text-yellow-800`}>Degraded</span>;
  }
  if (status === "Critical") {
    return <span className={`${base} bg-red-100 text-red-800`}>Critical</span>;
  }
  return <span className={`${base} bg-slate-100 text-slate-600`}>{status}</span>;
}

function EnvCardView({ card, state }: { card: EnvCard; state: CardState }) {
  const stopped = card.azureState === "Stopped";
  const status = state.data?.status;

  let borderColor: string;
  let headerBg: string;
  if (stopped) {
    borderColor = "border-slate-200";
    headerBg = "bg-slate-100";
  } else if (!state.data) {
    borderColor = "border-slate-200";
    headerBg = "bg-slate-50";
  } else if (status === "Healthy") {
    borderColor = "border-green-300";
    headerBg = "bg-green-50";
  } else if (status === "Degraded") {
    borderColor = "border-yellow-300";
    headerBg = "bg-yellow-50";
  } else if (status === "Critical") {
    borderColor = "border-red-300";
    headerBg = "bg-red-50";
  } else {
    borderColor = "border-slate-200";
    headerBg = "bg-slate-50";
  }

  const repoName = GITHUB_REPOS[card.service];
  const sha = state.data?.build?.sha;
  const shortSha = state.data?.build?.shortSha;
  const builtAt = state.data?.build?.builtAt;
  const ageMinutes = state.data?.build?.ageMinutes;

  const commitUrl =
    sha && sha !== "dev"
      ? `https://github.com/${repoName}/commit/${sha}`
      : null;

  return (
    <div className={`border-2 ${borderColor} rounded-lg overflow-hidden shadow-sm ${stopped ? "opacity-60" : ""}`}>
      <div className={`${headerBg} px-4 py-3 flex items-center justify-between gap-2`}>
        <div className="min-w-0">
          <span className="font-semibold text-sm text-slate-800">{card.label}</span>
          {card.slotName && (
            <span className="ml-2 text-xs text-slate-500 font-mono">{card.slotName}</span>
          )}
        </div>
        {stopped ? (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-200 text-slate-600 shrink-0">
            Stopped
          </span>
        ) : state.loading && !state.data ? (
          <span className="text-xs text-slate-400 shrink-0">Loading...</span>
        ) : state.data ? (
          <StatusBadge status={state.data.status} />
        ) : (
          <span className="inline-block px-2 py-0.5 rounded text-xs font-semibold bg-slate-100 text-slate-500 shrink-0">
            Unreachable
          </span>
        )}
      </div>

      <div className="px-4 py-3 space-y-2 text-sm">
        {state.error && !state.data && (
          <p className="text-red-600 text-xs">{state.error}</p>
        )}

        {state.data?.build ? (
          <>
            <div className="flex items-center gap-2">
              <span className="text-slate-500 w-20 shrink-0">Commit</span>
              {commitUrl ? (
                <a
                  href={commitUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-blue-600 hover:underline"
                >
                  {shortSha ?? "—"}
                </a>
              ) : (
                <span className="font-mono text-slate-400">{shortSha ?? "dev"}</span>
              )}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-slate-500 w-20 shrink-0">Built</span>
              <span className="text-slate-700">
                {builtAt && builtAt !== "unknown"
                  ? `${formatAge(ageMinutes)} ago — ${formatAbsolute(builtAt)}`
                  : "unknown"}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-slate-500 w-20 shrink-0">Age</span>
              <span className="text-slate-700">{formatAge(ageMinutes)}</span>
            </div>
          </>
        ) : state.data ? (
          <p className="text-slate-400 text-xs italic">
            Build info not available (deploy before this feature shipped)
          </p>
        ) : null}

        {state.lastChecked && (
          <div className="flex items-center gap-2 pt-1 border-t border-slate-100">
            <span className="text-slate-400 w-20 shrink-0 text-xs">Checked</span>
            <span className="text-slate-400 text-xs">{formatRelative(state.lastChecked)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Deployments() {
  const [cards, setCards] = useState<CardState[]>(
    ENVIRONMENTS.map(() => ({ loading: true, error: null, data: null, lastChecked: null }))
  );
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL_MS / 1000);

  const fetchAll = useCallback(async () => {
    setCards(prev => prev.map(c => ({ ...c, loading: true })));

    const results = await Promise.allSettled(
      ENVIRONMENTS.map(async (env) => {
        if (env.azureState === "Stopped") {
          throw new Error("Stopped");
        }
        const resp = await fetch(env.url, {
          signal: AbortSignal.timeout(10_000)
        });
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        return (await resp.json()) as HealthResponse;
      })
    );

    const now = new Date();
    setCards(
      results.map((result) => {
        if (result.status === "fulfilled") {
          return {
            loading: false,
            error: null,
            data: result.value,
            lastChecked: now
          };
        } else {
          return {
            loading: false,
            error: String((result as PromiseRejectedResult).reason),
            data: null,
            lastChecked: now
          };
        }
      })
    );
    setLastRefresh(now);
    setCountdown(REFRESH_INTERVAL_MS / 1000);
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Auto-refresh timer
  useEffect(() => {
    const interval = setInterval(fetchAll, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchAll]);

  // Countdown display
  useEffect(() => {
    const tick = setInterval(() => {
      setCountdown(prev => (prev <= 1 ? REFRESH_INTERVAL_MS / 1000 : prev - 1));
    }, 1000);
    return () => clearInterval(tick);
  }, []);

  const stagingCards = cards.slice(0, 2);
  const prodLiveCards = cards.slice(2, 4);
  const prodSlotCards = cards.slice(4);
  const stagingEnvs = ENVIRONMENTS.slice(0, 2);
  const prodLiveEnvs = ENVIRONMENTS.slice(2, 4);
  const prodSlotEnvs = ENVIRONMENTS.slice(4);

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold text-slate-900">Deployments</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Build SHA and age for each environment. Auto-refreshes every 30s.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-400">
            Refreshing in {countdown}s
            {lastRefresh && ` · Last: ${formatRelative(lastRefresh)}`}
          </span>
          <button
            onClick={fetchAll}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 text-slate-600"
          >
            Refresh now
          </button>
        </div>
      </div>

      <div className="mb-6">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Staging
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {stagingEnvs.map((env, i) => (
            <EnvCardView key={env.url} card={env} state={stagingCards[i]} />
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Production — Live
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {prodLiveEnvs.map((env, i) => (
            <EnvCardView key={env.url} card={env} state={prodLiveCards[i]} />
          ))}
        </div>
      </div>

      <div className="mt-6">
        <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-3">
          Production — Deployment Slots
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {prodSlotEnvs.map((env, i) => (
            <EnvCardView key={env.url} card={env} state={prodSlotCards[i]} />
          ))}
        </div>
      </div>

      <SignalRPanel />
    </div>
  );
}
